import { Component, computed, effect, inject, signal } from '@angular/core';
import { form, minLength, required, FormField } from '@angular/forms/signals';
import { ImageService } from '../../../../services/image/image.service';
import { S3uploaderService } from '../../../../services/s3uploader/s3uploader.service';
import { firstValueFrom, take } from 'rxjs';
import { ImageTypesEnum, S3BucketEnum } from '../../../../common/general-types/media-assets.dto';
import { BusinessAccountService } from '../../../../services/business-account/business-account.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { OnInit } from '@angular/core';
import { LoadingOverlayComponent } from '../../../../atoms/loading-overlay/loading-overlay.component';

@Component({
  selector: 'mb-brand-account',
  standalone: true,
  imports: [FormField, LoadingOverlayComponent],
  templateUrl: './brand-account.component.html',
  styleUrl: './brand-account.component.css'
})
export class BrandAccountComponent implements OnInit {

  readonly operatingCurrencies = [
    { code: 'GBP', symbol: '£', label: 'British Pound' },
    { code: 'USD', symbol: '$', label: 'US Dollar' },
    { code: 'EUR', symbol: '€', label: 'Euro' },
  ] as const;

  businessInformationModel = signal({
    companyName: '',
    website: '',
    instagram: '',
    tiktok: '',
    about: '',
    coverPhoto: '',
    companyLogo: '',
    currencyCode: '',
  });
  businessInformationForm = form(this.businessInformationModel, (schemaPath) => {
    required(schemaPath.companyName, { message: 'Company name is required' });
    minLength(schemaPath.companyName, 2, { message: 'Company name must be at least 2 characters long' });
    required(schemaPath.website, { message: 'Website is required' });
  })
  businessInformationUpdated = signal<boolean>(false);
  private isInitialized = false;


  /** Selected Profile Pictures */
  selectedFiles: any[] = [];
  companyLogo = signal<any>('');
  imageLoading: boolean = false;

  coverPhotoLoading = signal(false);
  coverPhotoError = signal<string | null>(null);
  coverPhotoBroken = signal(false);
  coverDragging = signal(false);

  private readonly maxCoverPhotoBytes = 10 * 1024 * 1024;
  private readonly coverPhotoMimeTypes = ['image/png', 'image/jpeg', 'image/gif'];

  S3Bucket = S3BucketEnum;
  ImageTypes = ImageTypesEnum;

  updatingBusinessInformation = signal<boolean>(false);

  selectedCurrencySymbol = computed(() => {
    const code = this.businessInformationModel().currencyCode?.toUpperCase();
    return this.operatingCurrencies.find((currency) => currency.code === code)?.symbol ?? '£';
  });

  selectedCurrencyLabel = computed(() => {
    const code = this.businessInformationModel().currencyCode?.toUpperCase();
    const currency = this.operatingCurrencies.find((item) => item.code === code);
    return currency ? `${currency.code} — ${currency.label}` : 'GBP — British Pound';
  });


  /** Services */
  private currentUserService = inject(CurrentUserService);
  private businessAccountService = inject(BusinessAccountService);
  private uploadService = inject(S3uploaderService);
  public imageService = inject(ImageService); 
  /** Services - END */

  
  constructor() {
    effect(() => {
      const model = this.businessInformationModel();
      if (this.isInitialized) {
        this.businessInformationUpdated.set(true);
      }
    });    
  }

  ngOnInit(): void {
    this.businessInformationModel.set({
      companyName: this.currentUserService.focussedBrand.companyName,
      website: this.currentUserService.focussedBrand.website,
      instagram: this.currentUserService.focussedBrand.instagram,
      tiktok: this.currentUserService.focussedBrand.tiktok,
      about: this.currentUserService.focussedBrand.about,
      coverPhoto: this.currentUserService.focussedBrand.coverPhoto ?? '',
      companyLogo: this.currentUserService.focussedBrand.companyLogo,
      currencyCode: this.normalizeCurrencyCode(this.currentUserService.focussedBrand.currencyCode),
    });
    this.companyLogo.set(this.currentUserService.focussedBrand.companyLogo ?? '');
    this.isInitialized = true;
  }



  selectFile(event: any): void {
    this.selectedFiles = event.target.files;
    this.upload(this.selectedFiles);
  }

  async upload(selectedFiles: any) {
    this.imageLoading = true; 
    const uploadedFile: { file: string, fileKey: string } | null = await this.uploadService.uploadFile(selectedFiles, this.S3Bucket.PROFILE_PHOTOS, this.ImageTypes.GALLERY);

    if (uploadedFile) {
      this.companyLogo.set(uploadedFile.fileKey);
      try {
        const user_updated: any = await firstValueFrom(this.businessAccountService.updateBusinessAccountPartial(this.currentUserService.focussedBrand.id, { companyLogo: this.companyLogo() }));
        this.companyLogo.set(user_updated.companyLogo);
        this.currentUserService.setFocussedBrand(user_updated);
      } catch (error) {
        console.error('Error updating user:', error);
      }
      
    } else {
      console.error('No uploaded file');
    }
    this.imageLoading = false;
  }

  selectCoverFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0) ?? null;
    input.value = '';
    if (!file || this.coverPhotoLoading()) {
      return;
    }
    void this.uploadCoverPhoto(file);
  }

  onCoverDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.coverDragging.set(true);
  }

  onCoverDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const current = event.currentTarget as HTMLElement | null;
    const next = event.relatedTarget as Node | null;
    if (current && next && current.contains(next)) {
      return;
    }
    this.coverDragging.set(false);
  }

  onCoverPhotoError(): void {
    this.coverPhotoBroken.set(true);
  }

  onCoverDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.coverDragging.set(false);
    const file = event.dataTransfer?.files.item(0) ?? null;
    if (!file || this.coverPhotoLoading()) {
      return;
    }
    void this.uploadCoverPhoto(file);
  }

  async uploadCoverPhoto(file: File): Promise<void> {
    const validationError = this.coverPhotoFileError(file);
    if (validationError) {
      this.coverPhotoError.set(validationError);
      return;
    }

    const brandId = this.currentUserService.focussedBrand?.id as string | undefined;
    if (!brandId) {
      this.coverPhotoError.set('Cover photo could not be saved. Try again.');
      return;
    }

    this.coverPhotoError.set(null);
    this.coverPhotoLoading.set(true);

    const selectedFiles = {
      length: 1,
      item: (index: number) => (index === 0 ? file : null),
    } as FileList;

    const uploadedFile = await this.uploadService.uploadFile(
      selectedFiles,
      this.S3Bucket.COVER_PHOTOS,
      this.ImageTypes.GALLERY_16_9,
    );

    if (!uploadedFile) {
      this.coverPhotoError.set('Cover photo could not be uploaded. Try again.');
      this.coverPhotoLoading.set(false);
      return;
    }

    try {
      const updated = await firstValueFrom(
        this.businessAccountService.updateBusinessAccountPartial(brandId, {
          coverPhoto: uploadedFile.fileKey,
        }),
      ) as { coverPhoto?: string | null };
      const coverPhoto = updated.coverPhoto || uploadedFile.fileKey;
      this.coverPhotoBroken.set(false);
      this.businessInformationModel.update((current) => ({ ...current, coverPhoto }));
      this.currentUserService.setFocussedBrand(updated);
    } catch (error) {
      console.error('Error updating cover photo:', error);
      this.coverPhotoError.set('Cover photo uploaded, but the brand could not be updated. Try again.');
    } finally {
      this.coverPhotoLoading.set(false);
    }
  }

  private coverPhotoFileError(file: File): string | null {
    if (!this.coverPhotoMimeTypes.includes(file.type)) {
      return 'Cover photo must be a PNG, JPG, or GIF.';
    }
    if (file.size > this.maxCoverPhotoBytes) {
      return 'Cover photo must be 10MB or smaller.';
    }
    return null;
  }

  saveChanges() {
    console.log('saveChanges()');

    let businessInformationTouched = this.businessInformationForm.companyName().touched() || this.businessInformationForm.website().touched() || this.businessInformationForm.instagram().touched() || this.businessInformationForm.tiktok().touched() || this.businessInformationForm.about().touched();

    if (businessInformationTouched && this.businessInformationUpdated()) {
      console.log('Saving business information');
      this.updatingBusinessInformation.set(true);
      this.businessAccountService.updateBusinessAccountPartial(this.currentUserService.focussedBrand.id, {
        companyName: this.businessInformationModel().companyName,
        website: this.businessInformationModel().website,
        instagram: this.businessInformationModel().instagram,
        tiktok: this.businessInformationModel().tiktok,
        about: this.businessInformationModel().about,
      }).pipe(take(1)).subscribe((businessAccount: any) => {
        console.log('Business account updated:', businessAccount);
        this.currentUserService.setFocussedBrand(businessAccount);
        setTimeout(() => {
          this.updatingBusinessInformation.set(false);
        }, 500);
      });
    }
  }

  private normalizeCurrencyCode(code: string | undefined | null): string {
    const upper = (code ?? 'GBP').toUpperCase();
    return this.operatingCurrencies.some((currency) => currency.code === upper) ? upper : 'GBP';
  }
}
